// built-from: /canonical-domain-scenarios v1 impl:canonical-domain-scenarios role:implementation
import type { SessionData } from '../../hooks/sessionTypes';
import type { StreamWorkspaceStream } from '../../components/streamMappingWorkspaceTypes';
import type { VsmStep } from '../../utils/flowModel';
import { davidMorgan, kevinOSullivan } from './archetypes';
import { currentValueStreamModel, mapCurrentValueStreams } from './activities';
import { mapPriorityValueStreams } from './scenarios';

function freezeList<T>(items: T[]): T[] {
  return Object.freeze(items) as T[];
}

export const lumenGridPriorityValueStream: StreamWorkspaceStream = Object.freeze({
  id: 'customer-onboarding-first-vend',
  name: 'Customer onboarding and first vend',
  oneLiner: 'From an accepted customer application to the first successful energy vend.',
  smes: [{ name: kevinOSullivan.name, contact: kevinOSullivan.role }],
  mappingSessionId: 'lumen-grid-vsm-session-01',
  sequenceOverlay: Object.freeze({
    steps: freezeList([
      { order: 1, statementId: 1, label: 'Register customer and verify identity' },
      { order: 2, statementId: 2, label: 'Configure the prepay meter' },
      { order: 3, statementId: 3, label: 'Complete the first vend' },
    ]),
  }),
  painLinks: freezeList([
    { statementId: 2, text: 'Meter configuration waits for data to be rekeyed between teams.' },
  ]),
});

export const lumenGridCurrentStateMappingSetup = Object.freeze({
  question: mapPriorityValueStreams.question,
  streamName: lumenGridPriorityValueStream.name,
  startBoundary: 'Customer application accepted',
  endBoundary: 'First successful energy vend',
  participants: mapPriorityValueStreams.participants.map((participant) => participant.name).join(', '),
  reviewer: mapPriorityValueStreams.reviewer.name,
});

export const lumenGridValueStreamMappingSession: SessionData = Object.freeze({
  id: 'lumen-grid-vsm-session-01',
  createdAt: '2026-07-08T09:00:00.000Z',
  updatedAt: '2026-07-08T11:15:00.000Z',
  conversationHistory: freezeList<SessionData['conversationHistory'][number]>([
    {
      id: 'vsm-message-01',
      type: 'agent',
      text: 'Where does the customer wait after their application is accepted?',
      timestamp: '10:42',
    },
    {
      id: 'vsm-message-02',
      type: 'user',
      text: 'Operations rekeys the meter details and the customer usually waits two days.',
      timestamp: '10:44',
    },
  ]),
  domainDictionary: { BoundedContexts: [] },
  mode: 'discovery',
  phase: 'refine',
  spine: freezeList<NonNullable<SessionData['spine']>[number]>([
    { order: 1, label: 'Register customer and verify identity', state: 'unconfirmed' },
    { order: 2, label: 'Configure the prepay meter', state: 'unconfirmed' },
    { order: 3, label: 'Complete the first vend', state: 'unconfirmed' },
  ]),
  newModel: {
    flowText: '',
    openQuestions: 'Confirm whether failed meter configuration attempts are visible to Customer Care.',
    nextQuestion: 'What causes the meter configuration queue to age?',
    raw: '',
    frozenBoundary: 3,
    vsm: {
      processes: [
        {
          id: 'customer-onboarding-first-vend',
          label: 'Customer onboarding and first vend',
          steps: [
            { statementId: 1, ct: '25m', wt: '4h', ca: 96 },
            {
              statementId: 2,
              ct: '18m',
              wt: '2d',
              ca: 82,
              waitReason: 'Meter data is rekeyed between Operations and the vending platform.',
            } as VsmStep & { waitReason: string },
            { statementId: 3, ct: '8m', wt: '3h', ca: 91 },
          ],
        },
      ],
    },
  },
});

export type ActivityOutputReviewState = 'ready-for-review' | 'changes-requested' | 'reviewed';

export type LumenGridCurrentValueStreamOutput = {
  readonly id: string;
  readonly activityId: string;
  readonly name: string;
  readonly method: string;
  readonly state: ActivityOutputReviewState;
  readonly revision: number;
  readonly returnedAt: string;
  readonly reviewer: typeof davidMorgan;
  readonly contributors: ReadonlyArray<typeof davidMorgan | typeof kevinOSullivan>;
  readonly sessionIds: ReadonlyArray<string>;
  readonly summary: string;
  readonly assumptions: ReadonlyArray<string>;
  readonly openQuestions: ReadonlyArray<string>;
};

export const lumenGridCurrentValueStreamOutputAwaitingReview: LumenGridCurrentValueStreamOutput = Object.freeze({
  id: 'lumen-grid-current-value-stream-output-01',
  activityId: 'lumen-grid-map-priority-value-streams',
  name: currentValueStreamModel.name,
  method: mapCurrentValueStreams.method,
  state: 'ready-for-review',
  revision: 1,
  returnedAt: '2026-07-08T11:15:00.000Z',
  reviewer: davidMorgan,
  contributors: Object.freeze([davidMorgan, kevinOSullivan]),
  sessionIds: Object.freeze([lumenGridValueStreamMappingSession.id]),
  summary: 'The first-vend journey contains 51 minutes of work and more than two days of waiting, concentrated in meter configuration.',
  assumptions: Object.freeze([
    'The measured sample represents standard residential onboarding.',
    'Manual exception handling was described but not timed.',
  ]),
  openQuestions: Object.freeze([
    'How often does meter configuration fail on the first attempt?',
    'Can the vending platform accept meter data without Operations rekeying it?',
  ]),
});

export function buildLumenGridCurrentValueStreamOutput(
  overrides: Partial<LumenGridCurrentValueStreamOutput> = {},
): LumenGridCurrentValueStreamOutput {
  return Object.freeze({ ...lumenGridCurrentValueStreamOutputAwaitingReview, ...overrides });
}
